<template lang="pug">
div
  sorting-modal(
    v-model="customFieldsToSort"
    ref="fieldSortingModal"
    sorting-field="rank"
    label-field="name"
    :resource="pluralResource"
    @input="handleSort"
  )
  resource-table(
    v-if="shouldRenderTable"
    item-key="id"
    disable-edit
    :can-show-admin-resources="showAdminResources"
    :can-manage-own-resources="isSaveEnabled"
    :show-sort-action="isSaveEnabled"
    :headers="headers"
    :items="customFieldsData"
    :singular-resource="singularResource"
    :plural-resource="pluralResource"
    :place-holder-item="updatePlaceHolder"
    :should-save="isFormValid"
    :selected-label="selectedLabel"
    :is-loading="isLoading"
    :affected-count="affectedContacts"
    :show-success-message="showSuccess"
    :show-error-message="showError"
    @create="handleCreate"
    @cancel="handleCancel"
    @delete="handleDelete"
    @selected-item="setSelectedId"
    @clear-messages="clearMessages"
    @save:create="handleSaveCreate"
    @sort="openSortModal"
  )
    template(slot="items" slot-scope="props")
      td
        span {{ props.item.rank }}
      td
        editable-text-field(
          v-model="props.item.name"
          :is-editing="shouldEdit(props.item)"
          :rules="[rules.required, rules.noEmpty, rules.maxLength, isUniqueField]"
          @update:error="handleError"
        )
      td
        editable-drop-down(
          v-model="props.item.data_type"
          :is-editing="shouldEdit(props.item)"
          :items="sysDataTypes"
          :rules="[rules.required]"
          @update:error="handleError"
        )
      td
        span {{ props.item.use_count }}
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { CONTACTS } from '../config';

import ResourceTable from './ResourceTable';
import EditableTextField from './EditableTextField';
import EditableDropDown from './EditableDropDown';
import GetSubAccountPermissions from '../mixins/getSubAccountPermissions';
import GetAffectedContacts from '../mixins/getAffectedContacts';
import SortingModal from './SortingModal';

const {
  mapActions: mapCustomFieldsActions,
  mapState: mapCustomFieldsState,
  mapMutations: mapCustomFieldsMutations,
} = createNamespacedHelpers('customFieldsAccountManagement');

export default {
  name: 'CustomFields',
  mixins: [GetSubAccountPermissions, GetAffectedContacts],
  components: {
    ResourceTable,
    EditableTextField,
    EditableDropDown,
    SortingModal,
  },
  data() {
    return {
      shouldRenderTable: false,
      resourcePermissionsType: CONTACTS.customFields.resourceType,
      selectedId: null,
      isFormValid: true,
      singularResource: CONTACTS.customFields.singularResource,
      pluralResource: CONTACTS.customFields.pluralResources,
      headers: CONTACTS.customFields.headers,
      placeHolderItem: CONTACTS.customFields.placeHolderItem,
      rules: {
        required: v => !!v || 'Required',
        noEmpty: v => (v && v.trim().length > 0) || 'Custom field must be not-empty',
        maxLength: v => (v && v.length <= 255) || 'Custom field must be less than 255 characters',
      },
      isDelete: false,
      isSave: false,
      sysDataTypes: [],
    };
  },
  async mounted() {
    await this.findCustomFields();
    await this.findSysDataTypes();
    this.shouldRenderTable = true;
    this.clearMessages();
    this.sysDataTypes = this.dataTypes.map(item => item.name);
  },
  watch: {
    async selectedId(value) {
      if (value) {
        const query = this.buildQuery(value);
        await this.findTotalContactsByResource(query);
      }
      this.isFormValid = true;
    },
  },
  computed: {
    ...mapCustomFieldsState([
      'data',
      'dataTypes',
      'hasError',
      'errorMsg',
      'hasSuccess',
      'isLoading',
    ]),
    customFieldsData() {
      return this.data;
    },
    customFieldsToSort() {
      return this.customFieldsData.filter(
        field => !field.disabled_by || (field.disabled_by && field.disabled_by === 'system'),
      );
    },
    selectedLabel() {
      const item = this.data.find(i => i.id === this.selectedId);
      return item && item.name;
    },
    showError: {
      get() {
        return this.hasError;
      },
      set(value) {
        this.clearMessages();
        this.clearAffectedContactsMessage();
      },
    },
    showSuccess: {
      get() {
        return this.hasSuccess;
      },
      set(value) {
        this.clearMessages();
        this.clearAffectedContactsMessage();
      },
    },
    updatePlaceHolder() {
      const item = Object.assign({}, this.placeHolderItem);
      if (this.data.length) {
        item.rank += this.data[this.data.length - 1].rank;
      }
      return item;
    },
  },
  methods: {
    ...mapCustomFieldsActions([
      'findCustomFields',
      'findSysDataTypes',
      'saveCustomField',
      'updateRanks',
      'deleteCustomField',
    ]),
    ...mapCustomFieldsMutations(['clearMessages']),
    shouldEdit(item) {
      const { id } = item;
      return this.selectedId === id && this.isSave && !this.isDelete;
    },
    handleCancel() {
      this.selectedId = null;
      this.isDelete = false;
      this.isSave = false;
    },
    handleCreate() {
      this.isSave = true;
      this.selectedId = 0;
    },
    async handleSaveCreate(item) {
      this.isSave = false;
      item.name = item.name.trim();
      await this.saveCustomField(item);
      await this.findCustomFields();
    },
    async handleDelete(item) {
      this.selectedId = null;
      this.isDelete = false;
      await this.deleteCustomField(item.id);
      await this.findCustomFields();
    },
    handleError(value) {
      this.isFormValid = !value;
    },
    setSelectedId(itemId) {
      this.isDelete = true;
      this.selectedId = itemId;
    },
    isUniqueField(nameVal) {
      const duplicated =
        nameVal &&
        this.data.find(item => {
          return (
            (!item.disabled_by || (item.disabled_by && item.disabled_by === 'system')) &&
            item.name.toLowerCase() === nameVal.trim().toLowerCase() &&
            item.id !== this.selectedId
          );
        });

      if (duplicated) return 'Custom field must be unique';
      return true;
    },
    buildQuery(id) {
      const customField = `custom_field_${id}`;
      const query = {};
      // Currently there is a bug when you try to find a contact with a custom field (no empty)
      // that return a 500 status
      // temporarily use this solution
      // TO DO: replace this with the no empty query search when the bug is fixed
      query[customField] = {
        $like: '**',
      };
      return query;
    },
    async handleSort(items) {
      const ids = items.map(item => item.id);
      const payload = {
        ids,
      };
      await this.updateRanks(payload);
      await this.findCustomFields();
    },
    openSortModal() {
      this.$refs.fieldSortingModal.modal_open();
    },
  },
};
</script>
